import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaEnvelope, FaTimes, FaCheckCircle } from "react-icons/fa";
import { GoogleLogin } from "@react-oauth/google";
import { useAuth } from "../../contexts/AuthContext";
import { trackLoginAttempt, trackLoginSuccess, trackLoginFail } from "../../utils/analytics";
import { appendCompanyCode } from "../../hooks/useCompanyCode";
import "./EmailOTPModal.css";

/**
 * Reusable Email OTP Login Modal Component
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Called when modal is closed
 * @param {function} onLoginSuccess - Called after successful login with (email, customerToken)
 * @param {string} redirectTo - Optional path to redirect to after login (if not using onLoginSuccess)
 */
export default function EmailOTPModal({ isOpen, onClose, onLoginSuccess, redirectTo }) {
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
  const navigate = useNavigate();
  const { setShowLoading } = useAuth();

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const googleButtonRef = useRef(null);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmail("");
      setEmailError("");
      setOtpCode("");
      setOtpError("");
      setOtpSent(false);
      setResendCooldown(0);
      setShowLoading(false);
    }
  }, [isOpen, setShowLoading]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setEmailError("");
    setOtpError("");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    setSendingOtp(true);

    // Track login attempt
    trackLoginAttempt('email_otp', email);

    try {
      const response = await fetch(`${apiUrl}/loyalty/email-otp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to send OTP");
      }
      setOtpSent(true);
      setResendCooldown(60);
    } catch (error) {
      console.error("Error sending OTP:", error);
      setEmailError(
        error.message || "Failed to send verification code. Please try again."
      );
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setOtpError("");
    if (!otpCode || otpCode.length !== 6) {
      setOtpError("Please enter the complete 6-digit verification code");
      return;
    }
    setVerifyingOtp(true);
    try {
      const response = await fetch(`${apiUrl}/loyalty/email-otp/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: otpCode }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Invalid verification code");
      }
      const verifyData = await response.json();

      if (!verifyData.customer_exists) {
        // New customer - Register them immediately after email verification
        const companyCode = sessionStorage.getItem("company_signup_code");

        // Call complete-signup to register the customer NOW
        const completeSignupBody = { email };
        if (companyCode) {
          completeSignupBody.code = companyCode;
        }

        const signupResponse = await fetch(`${apiUrl}/loyalty/complete-signup`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(completeSignupBody),
        });

        if (!signupResponse.ok) {
          const errorData = await signupResponse.json();
          throw new Error(errorData.detail || "Failed to complete signup");
        }

        const signupData = await signupResponse.json();

        // Save auth tokens immediately
        const timestamp = new Date().toISOString();
        sessionStorage.setItem("loyalty_email", email);
        sessionStorage.setItem("loyalty_email_verified", timestamp);
        sessionStorage.setItem("loyalty_auth_token", signupData.auth_token);
        localStorage.setItem("loyalty_email", email);
        localStorage.setItem("loyalty_email_verified", timestamp);
        localStorage.setItem("last_activity", timestamp);

        // Track successful login for new customer
        trackLoginSuccess(signupData.auth_token, 'email_otp');

        // Call success callback or redirect to home (skip questionnaire)
        if (onLoginSuccess) {
          onLoginSuccess(email, signupData.auth_token);
        } else if (redirectTo) {
          navigate(redirectTo);
        } else {
          navigate(appendCompanyCode("/perks-directory"));
        }

        onClose();
        return;
      }

      // Existing customer - get token
      const tokenResponse = await fetch(
        `${apiUrl}/coupon/customer-token?email=${encodeURIComponent(email)}`
      );
      if (!tokenResponse.ok) {
        throw new Error("Failed to get customer token");
      }
      const tokenData = await tokenResponse.json();
      const customerToken = tokenData.customer_token;

      // Save to sessionStorage and localStorage
      const timestamp = new Date().toISOString();
      sessionStorage.setItem("loyalty_email", email);
      sessionStorage.setItem("loyalty_email_verified", timestamp);
      localStorage.setItem("loyalty_email", email);
      localStorage.setItem("loyalty_email_verified", timestamp);

      // Track successful login for existing customer
      trackLoginSuccess(customerToken, 'email_otp');

      // Call success callback or redirect
      if (onLoginSuccess) {
        onLoginSuccess(email, customerToken);
      } else if (redirectTo) {
        navigate(redirectTo);
      }

      onClose();
    } catch (error) {
      console.error("Error verifying OTP:", error);

      // Track failed login
      trackLoginFail('email_otp', error.message);

      setOtpError(
        error.message || "Invalid verification code. Please try again."
      );
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0 || sendingOtp) return;
    setOtpError("");
    setSendingOtp(true);
    try {
      const response = await fetch(`${apiUrl}/loyalty/email-otp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to resend OTP");
      }
      setResendCooldown(60);
    } catch (error) {
      console.error("Error resending OTP:", error);
      setOtpError(error.message || "Failed to resend code. Please try again.");
    } finally {
      setSendingOtp(false);
    }
  };

  // Handle Google credential response
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setEmailError("");
      setShowLoading(true);

      // Track Google login attempt
      trackLoginAttempt('google');

      const companyCode = sessionStorage.getItem("company_signup_code");

      const response = await fetch(`${apiUrl}/loyalty/google-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_token: credentialResponse.credential,
          code: companyCode || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Google login failed");
      }

      const data = await response.json();

      // Log in all users directly (both new and existing)
      const timestamp = new Date().toISOString();
      sessionStorage.setItem("loyalty_email", data.email);
      sessionStorage.setItem("loyalty_email_verified", timestamp);
      sessionStorage.setItem("loyalty_auth_token", data.auth_token);
      localStorage.setItem("loyalty_email", data.email);
      localStorage.setItem("loyalty_email_verified", timestamp);
      localStorage.setItem("last_activity", timestamp);

      // Track successful Google login
      trackLoginSuccess(data.auth_token, 'google');

      if (onLoginSuccess) {
        onLoginSuccess(data.email, data.auth_token);
      } else if (redirectTo) {
        navigate(redirectTo);
      } else {
        navigate(appendCompanyCode("/perks-directory"));
      }

      onClose();
      setShowLoading(false);
    } catch (error) {
      console.error("Error during Google login:", error);

      // Track failed Google login
      trackLoginFail('google', error.message);

      setEmailError(error.message || "Google login failed. Please try again or use email verification.");
      setShowLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.error("Google login failed");

    // Track Google login failure
    trackLoginFail('google', 'Google login failed');

    setEmailError("Google login failed. Please try again or use email verification.");
    setShowLoading(false);
  };

  // Don't render modal when not open (loading screen is handled at App level)
  if (!isOpen) return null;

  return (
    <div className="loyalty-email-modal-overlay">
      <div className="loyalty-email-modal">
        <button
          onClick={onClose}
          className="loyalty-email-modal-close"
          aria-label="Close"
        >
          <FaTimes />
        </button>
        <div className="loyalty-email-modal-header">
          <FaEnvelope className="loyalty-email-icon" />
          <h2>Member Login</h2>
          <p>
            {otpSent
              ? "Enter the verification code sent to your email"
              : "Join Block Club Here"}
          </p>
        </div>

        {!otpSent ? (
          <form onSubmit={handleSendOTP} className="loyalty-email-form">
            <div className="social-login-section">
              {/* Hidden Google Login component for OAuth */}
              <div className="social-login-hidden">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={false}
                />
              </div>

              {/* Custom Google button */}
              <div className="google-login-wrapper">
                <button
                  type="button"
                  className="custom-google-btn"
                  ref={googleButtonRef}
                  onClick={() => {
                    // Trigger the hidden Google login
                    const googleBtn = document.querySelector('.social-login-section [role="button"]');
                    if (googleBtn) {
                      googleBtn.click();
                    }
                  }}
                >
                  <svg className="google-icon" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    <path fill="none" d="M0 0h48v48H0z"/>
                  </svg>
                  <span className="google-text">Google</span>
                </button>
              </div>

              <div className="social-login-text">
                <span>or enter your email</span>
              </div>
            </div>

            <div className="standard-form-group">
              <label className="standard-label">Email Address</label>
              <input
                type="email"
                className="standard-input"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError("");
                }}
                autoFocus
              />
              {emailError && (
                <div className="loyalty-email-error">
                  <FaTimes /> {emailError}
                </div>
              )}
            </div>
            <button
              type="submit"
              className="btn-primary modal-btn-primary"
              disabled={sendingOtp}
            >
              {sendingOtp ? (
                <>
                  <span className="btn-spinner"></span>
                  Sending Code...
                </>
              ) : (
                <>
                  <FaEnvelope />
                  Send Verification Code
                </>
              )}
            </button>

            <div className="loyalty-terms-agreement">
              <p>
                By clicking an option above, I agree to the{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer">
                  Terms and Conditions
                </a>{" "}
                and have read the{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Statement
                </a>.
              </p>
            </div>

          </form>
        ) : (
          <form onSubmit={handleVerifyOTP} className="loyalty-email-form">
            <div className="standard-form-group">
              <label className="standard-label">Verification Code</label>
              <input
                type="text"
                className="standard-input"
                placeholder="Enter 6-digit code"
                value={otpCode}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "");
                  setOtpCode(v);
                  setOtpError("");
                }}
                autoFocus
                maxLength={6}
                disabled={verifyingOtp}
                inputMode="numeric"
                pattern="[0-9]*"
              />
              {otpError && (
                <div className="loyalty-email-error">
                  <FaTimes /> {otpError}
                </div>
              )}
            </div>
            <button
              type="submit"
              className="btn-primary modal-btn-primary"
              disabled={verifyingOtp || otpCode.length !== 6}
            >
              {verifyingOtp ? (
                <>
                  <span className="btn-spinner"></span>
                  Verifying...
                </>
              ) : (
                <>
                  <FaCheckCircle />
                  Verify Code
                </>
              )}
            </button>
            <div className="loyalty-otp-resend">
              {resendCooldown > 0 ? (
                <p className="resend-timer">
                  Resend code in {resendCooldown}s
                </p>
              ) : (
                <p className="resend-link-container">
                  {sendingOtp ? (
                    <span className="resend-text-disabled">
                      <span className="btn-spinner-small"></span>
                      Sending code...
                    </span>
                  ) : (
                    <>
                      Didn't receive a code?{' '}
                      <button
                        type="button"
                        onClick={handleResendOTP}
                        className="resend-text-link"
                        disabled={sendingOtp}
                      >
                        Resend
                      </button>
                    </>
                  )}
                </p>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
